// No imports needed - we'll pass the translation function as a parameter

export interface ApiError {
  message: string;
  messageKey?: string;
  statusCode: number;
  timestamp: string;
  path: string;
  errors?: any;
}

export function getLocalizedErrorMessage(
  error: any,
  t: (key: string) => string,
): string {
  // Handle axios errors
  if (error?.response?.data) {
    const apiError: ApiError = error.response.data;

    // If we have a messageKey, use the localized message
    if (apiError.messageKey) {
      const localizedMessage = t(apiError.messageKey);
      // If translation exists, use it; otherwise fall back to the original message
      if (localizedMessage !== apiError.messageKey) {
        return localizedMessage;
      }
    }

    // Handle validation errors (array of messages)
    if (Array.isArray(apiError.message)) {
      // Try to map each validation message to a localized version
      const localizedMessages = apiError.message.map((msg) => {
        const messageKey = msg;
        if (messageKey) {
          const localizedMsg = t(messageKey);
          return localizedMsg !== messageKey ? localizedMsg : msg;
        }
        return msg;
      });
      return localizedMessages.join(", ");
    }

    // Try to map single error messages
    const messageKey = apiError.message;
    if (messageKey) {
      const localizedMessage = t(messageKey);
      if (localizedMessage !== messageKey) {
        return localizedMessage;
      }
    }

    // Fall back to the API message
    return apiError.message;
  }

  // Handle network errors
  if (error?.code === "NETWORK_ERROR" || error?.message === "Network Error") {
    return t("common.errors.serviceUnavailable");
  }

  // Handle timeout errors
  if (error?.code === "ECONNABORTED") {
    return t("common.errors.serviceUnavailable");
  }

  // Default fallback
  return error?.message || t("common.errors.internalServerError");
}

export function isAuthError(error: any): boolean {
  return error?.response?.status === 401;
}

export function isForbiddenError(error: any): boolean {
  return error?.response?.status === 403;
}

export function isValidationError(error: any): boolean {
  return error?.response?.status === 400;
}

export function isNotFoundError(error: any): boolean {
  return error?.response?.status === 404;
}

export function isConflictError(error: any): boolean {
  return error?.response?.status === 409;
}

export function isServerError(error: any): boolean {
  const status = error?.response?.status;
  return status >= 500 && status < 600;
}
